// ABOUTME: SPEC-53 — the two halves of the collection piece card, composition-agnostic: the gallery
// ABOUTME: (image, arrows, thumbnails, lens) and the details column (title, price, chevron-scrolled body).

import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { u, uMin, GLYPHS } from '../ReelChrome/railGeometry';
import { scrollGridByPage, readScrollMetrics, ScrollHint } from '../ReelChrome/panelScroll';
import { useMagnifierLens } from './useMagnifierLens';
import { priceOf, specGroups, specLabel, variantSubtitle } from './pieceEnvelope';

/** The panel name the card reports under; the backend's `scroll_panel(panel="PRODUCT")` targets it. */
export const CARD_PANEL = 'PRODUCT';

/**
 * Authored sizes (Figma 403:1448 right column `403:1452`, thumbnails `403:1464`), in the frame's px;
 * every one is rendered through `u()` against whichever `--u` the card root sets (desktop RAIL_UNIT
 * or mobile MOBILE_UNIT), so one table serves both compositions.
 */
export const CARD_TYPE = {
  title: 55, price: 42, subtitle: 37.333, heading: 37.333, body: 30, label: 28,
  action: 74.667, actionIcon: 40, thumb: 114, thumbGap: 20, arrow: 72, rule: 2.667, gap: 24,
};
const BODY_MIN_PX = 11;

function Rule({ margin }) {
  return <div className="bg-reel-chrome-textPrimary/30 shrink-0" style={{ height: u(CARD_TYPE.rule), margin: `${margin} 0` }} />;
}

function ArrowButton({ direction, onClick }) {
  const Icon = direction === 'previous' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={direction === 'previous' ? 'Previous image' : 'Next image'}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full bg-reel-chrome-panel/90 backdrop-blur-sm hover:bg-reel-chrome-panel transition-colors text-reel-chrome-textPrimary z-50"
      style={{ [direction === 'previous' ? 'left' : 'right']: u(12), width: uMin(CARD_TYPE.arrow, 32), height: uMin(CARD_TYPE.arrow, 32), pointerEvents: 'auto' }}
    >
      <Icon size={18} aria-hidden="true" />
    </button>
  );
}

/**
 * Left half: the selected image with prev/next (wrapping, as the grid's own arrows do), the strip
 * of ALL catalogue images, and — on hover-capable compositions — the grid's 220px / 250% lens.
 *
 * `thumbs` (SPEC-62 D-4): the strip drops out when the gallery box is too short to carry it without
 * squeezing the main image to thumbnail size — the caller (`pieceCardLayoutFor`) decides, this
 * component only obeys. Nothing is lost: the arrows already page the gallery whenever `count > 1`,
 * voice-driven `switch_gallery_image` moves the same index either way.
 *
 * @param {{ images: string[], index: number, onIndexChange: (i: number) => void, title: string,
 *           lens?: boolean, thumbs?: boolean }} props
 */
export function PieceGallery({ images, index, onIndexChange, title, lens = true, thumbs = true }) {
  const magnifier = useMagnifierLens();
  const count = images.length;
  const current = images[Math.min(index, Math.max(0, count - 1))];
  const step = (delta) => { if (count) onIndexChange((index + delta + count) % count); };
  const cross = lens && count > 0 && magnifier.capable; // no crosshair where no lens can follow

  return (
    <div className="flex flex-col h-full min-h-0" style={{ gap: u(CARD_TYPE.thumbGap) }}>
      <div
        ref={magnifier.containerRef}
        data-testid="piece-image-box"
        className="relative flex-1 min-h-0 flex items-center justify-center overflow-hidden rounded-lg group"
        style={{ cursor: cross ? 'crosshair' : 'default' }}
        {...(cross ? magnifier.handlers : {})}
      >
        {current && (
          <img
            src={current}
            alt={`${title || 'Piece'} view ${index + 1}`}
            draggable={false}
            data-testid="piece-main-image"
            className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
          />
        )}
        {cross && magnifier.visible && current && (
          <div data-testid="piece-lens" style={magnifier.lensStyle(current)} />
        )}
        {count > 1 && (
          <>
            <ArrowButton direction="previous" onClick={() => step(-1)} />
            <ArrowButton direction="next" onClick={() => step(1)} />
          </>
        )}
      </div>
      {thumbs && count > 1 && (
        <div className="flex shrink-0 overflow-x-auto custom-scrollbar" style={{ gap: u(CARD_TYPE.thumbGap) }} data-testid="piece-thumbs">
          {images.map((img, i) => {
            const active = i === index;
            return (
              <button
                key={`${img}-${i}`}
                type="button"
                aria-label={`Image ${i + 1}`}
                aria-current={active ? 'true' : undefined}
                data-testid="piece-thumb"
                onClick={() => onIndexChange(i)}
                className="shrink-0 overflow-hidden rounded-md transition-opacity"
                style={{
                  width: uMin(CARD_TYPE.thumb, 40), height: uMin(CARD_TYPE.thumb, 40),
                  border: `2px solid ${active ? 'rgba(243,240,233,0.95)' : 'transparent'}`,
                  opacity: active ? 1 : 0.6,
                  background: 'rgba(255,255,255,0.06)',
                  padding: 2,
                }}
              >
                <img src={img} alt="" className="w-full h-full object-contain" draggable={false} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Scrollbar-less, chevron-paged scrolling with the upstream `panel_scroll` report — the rail
 * panels' exact behaviour (panelScroll.js), keyed to the PRODUCT panel.
 */
export function useCardScroll({ signature, scrollCommand, onScrollStateChange, pageFallback = 240 }) {
  const ref = useRef(null);
  const [hint, setHint] = useState({ up: false, down: false });
  const reportRef = useRef(onScrollStateChange);
  reportRef.current = onScrollStateChange;

  const read = () => {
    const el = ref.current;
    const m = readScrollMetrics(el);
    if (el) setHint({ up: m.up, down: m.down });
    reportRef.current?.({ panel: CARD_PANEL, scrollable: m.overflowing, atTop: !m.up, atBottom: !m.down });
  };

  // A new piece starts at the top and re-reports (magic swap keeps the card mounted).
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = 0;
    read();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Forget on unmount so the next open re-reports (usePanelScrollReporter's dedupe contract).
  useEffect(() => () => reportRef.current?.({ panel: CARD_PANEL, forget: true }), []);

  useEffect(() => {
    if (!scrollCommand || String(scrollCommand.panel || '').toUpperCase() !== CARD_PANEL) return;
    scrollGridByPage(ref.current, scrollCommand.direction === 'up' ? 'up' : 'down', pageFallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollCommand?.nonce]);

  const page = (direction) => scrollGridByPage(ref.current, direction, pageFallback);
  return { ref, hint, read, page };
}

// Bare glyph, no chip — the same "frame truth" treatment SpecSheetOverlay's QuickAction gives the
// Vesper card's heart/bag (owner polish: these two panels' quick actions must read as one control,
// not a rail chip transplanted onto a product card). Sized by HEIGHT, width auto, so the heart and
// bag glyphs' different aspect ratios still land optically the same scale.
function QuickAction({ testId, label, glyph, onPress, pressed }) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      aria-pressed={pressed}
      onClick={onPress}
      className="flex items-center justify-center shrink-0"
      style={{
        width: uMin(CARD_TYPE.action, 36), height: uMin(CARD_TYPE.action, 36),
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
      }}
    >
      <img src={glyph.src} alt="" aria-hidden="true" style={{ height: uMin(CARD_TYPE.actionIcon, 18), width: 'auto' }} />
    </button>
  );
}

/**
 * Right half: title with heart + bag (Figma 403:1455), price, variant subtitle, then ONE column —
 * rule, Description, rule, Specification — that scrolls by chevrons only. No collapsibles, no
 * "Virtual Try On", no "C" tile, no right-hand buttons (owner brief).
 */
export function PieceDetails({
  product, wishlisted, inBag, onToggleWishlist, onAddToBag, scrollCommand, onScrollStateChange, leading,
}) {
  const scroll = useCardScroll({ signature: product?.id, scrollCommand, onScrollStateChange });
  const groups = specGroups(product);
  const subtitle = variantSubtitle(product);
  const price = priceOf(product);
  const gap = u(CARD_TYPE.gap);

  return (
    <div className="flex flex-col h-full min-h-0">
      {leading}
      <div className="flex items-start justify-between" style={{ gap }}>
        <h2 className="m-0 font-reel-heading min-w-0" data-testid="piece-title"
            style={{ fontSize: uMin(CARD_TYPE.title, 16), lineHeight: 1.15 }}>
          {product?.title}
        </h2>
        <div className="flex shrink-0" style={{ gap: u(16) }}>
          <QuickAction testId="piece-wishlist" pressed={!!wishlisted}
                       label={wishlisted ? 'Remove from favourites' : 'Save to favourites'}
                       active={!!wishlisted} glyph={wishlisted ? GLYPHS.savedFilled : GLYPHS.saved}
                       onPress={() => onToggleWishlist?.(product)} />
          <QuickAction testId="piece-bag" label={inBag ? 'In your bag — add again' : 'Add to bag'}
                       active={!!inBag} glyph={GLYPHS.bag} onPress={() => onAddToBag?.(product)} />
        </div>
      </div>
      {price && (
        <p className="m-0 font-reel-medium" data-testid="piece-price"
           style={{ fontSize: uMin(CARD_TYPE.price, 13), marginTop: u(12) }}>{price}</p>
      )}
      {subtitle && (
        <p className="m-0 font-reel-body text-reel-chrome-textDim" data-testid="piece-subtitle"
           style={{ fontSize: uMin(CARD_TYPE.subtitle, 12), marginTop: u(8) }}>{subtitle}</p>
      )}

      <div className="relative flex-1 min-h-0" style={{ marginTop: gap }}>
        {scroll.hint.up && <ScrollHint direction="up" onClick={() => scroll.page('up')} />}
        <div
          ref={scroll.ref}
          onScroll={scroll.read}
          data-testid="piece-scroll"
          className="custom-scrollbar absolute inset-0 overflow-y-auto overflow-x-hidden"
          style={{ fontSize: uMin(CARD_TYPE.body, BODY_MIN_PX), lineHeight: 1.45 }}
        >
          <Rule margin={u(8)} />
          <h3 className="m-0 font-reel-heading" style={{ fontSize: uMin(CARD_TYPE.heading, 12), marginTop: u(16) }}>Description</h3>
          {product?.description && (
            <p className="font-reel-body whitespace-pre-line" style={{ margin: `${u(12)} 0 ${u(20)}` }} data-testid="piece-description">
              {product.description}
            </p>
          )}
          <Rule margin={u(8)} />
          <h3 className="m-0 font-reel-heading" style={{ fontSize: uMin(CARD_TYPE.heading, 12), marginTop: u(16) }}>Specification</h3>
          {/* The envelope's attribute groups as they come, each under its own label — the grid's
              own reading (App.js buildDetailGroups / the "Further Details" panel), minus the
              legacy-spec append: a luxury card shows the tenant's curated groups and nothing else. */}
          <div data-testid="piece-specs" style={{ margin: `${u(12)} 0 ${u(8)}` }}>
            {groups.map((group, gi) => (
              <div key={group.label || gi} data-testid="piece-spec-group" style={{ marginTop: gi ? u(20) : 0 }}>
                {group.label && (
                  <div className="uppercase tracking-[0.2em] text-reel-chrome-textDim font-reel-medium" data-testid="piece-spec-group-label"
                       style={{ fontSize: uMin(CARD_TYPE.label * 0.8, 9), marginBottom: u(8) }}>{group.label}</div>
                )}
                <dl className="m-0 grid"
                    style={{ gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.4fr)', columnGap: u(32), rowGap: u(10) }}>
                  {group.rows.map((row) => (
                    <React.Fragment key={row.k}>
                      <dt className="text-reel-chrome-textDim font-reel-body m-0" style={{ fontSize: uMin(CARD_TYPE.label, BODY_MIN_PX) }}>{specLabel(row.k)}</dt>
                      <dd className="font-reel-medium m-0 break-words">{row.v}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
        {scroll.hint.down && <ScrollHint direction="down" onClick={() => scroll.page('down')} />}
      </div>
    </div>
  );
}
